<template>
	<div id="ProductsHomepageAppContainer" class="bs-slc">
	<modal-message
		v-if="stateMachine.loadingInitialData"
		:message="$t('loading_initial_data')"
	></modal-message>
	<LoginSelection
		v-else-if="showLoginSelection"
		@loginO365="login"
		@loginSuccess="handleLoginSuccess"
	/>
	<div v-else-if="stateMachine.accessError" class="access-denied">
		<h2>{{ $t('access_denied') }}</h2>
		<p>{{ $t('access_denied_message') }}</p>
	</div>
	<ReleaseNotes v-else-if="showReleaseNotesModal"
		id="release-notes" @cancelClicked="cancelClickedHandler"/>
	<router-view v-if="stateMachine.initialized"></router-view>
	</div>
</template>

<script>
import {
	mapState,
	mapMutations,
	mapActions,
	mapGetters,
} from 'vuex';

import ModalMessage from './components/ModalMessage.vue';
import ReleaseNotes from './modals/ReleaseNotes.vue';
import LoginSelection from './components/LoginSelection.vue';
import { getBrowserLocale } from './resources/i18n.js';

export default {
	name: 'ProductsHomepageApp',

	components: {
		'modal-message': ModalMessage,
		ReleaseNotes,
		LoginSelection,
	},

	data() {
		return {
			showReleaseNotesModal: null,
			showLoginSelection: false,
		};
	},

	computed: {
		...mapState(['stateMachine']),
		...mapGetters(['userListReleaseNotesCompleted']),
	},

	methods: {
		...mapMutations([
			'getLocalStorageData',
			'initUsageTracker',
			'setCategoryDisplayed',
			'setStateVariables',
			'transferCookiesToLocalStorage',
			'setQueryStringVariables',
			'setHeaderToken',
			'setCurrentUserId',
		]),
		...mapActions([
			'getUsageTrackerSessionInfo',
			'getStaticData',
			'flushUsageTrackerInfo',
		]),

		cancelClickedHandler() {
			this.showReleaseNotesModal = false;
		},
		removeCanvasMaxWidth() {
			let elements = document.querySelectorAll('.CanvasSection');
			if (elements.length === 1) {
				elements[0].parentElement.style.maxWidth = 'none';
			}
			if (this.stateMachine.verboseMode) console.log('[DP] CanvasSection max-width removed');
		},

		removeUnwantedElements() {
			let elements = ['PageTitle', 'Comments'],
				element,
				elementParent;
			elements
				.map((el) => `[data-sp-feature-tag="${el}"]`)
				.forEach((el) => {
					element = document.querySelectorAll(el);
					if (element && element.length) {
						// eslint-disable-next-line prefer-destructuring
						element = element[0];
						elementParent = element.parentElement;
						elementParent.remove(element);
						if (this.stateMachine.verboseMode) console.log(`[PMT] ${el} element removed`);
					} else if (this.stateMachine.verboseMode) console.log(`[PMT] ${el} element not found.`);
				});
		},

		setCssClasses() {
			let appElemFontFamily = getComputedStyle(document.getElementById('ProductsHomepageAppContainer')).getPropertyValue('font-family'),
				ss = [...document.styleSheets];
			ss.forEach((_ss, _ssIdx) => {
				if (!_ss.href && [..._ss.cssRules].find((rule) => rule.selectorText === '.tippy-tooltip')) {
					_ss.insertRule(`.tippy-tooltip { font-family: ${appElemFontFamily}; }`, _ss.cssRules.length);
					if (this.stateMachine.verboseMode) console.log(`[PRODUCTS] CSS rule inserted in styleSheet #${_ssIdx}`);
				}
			});
		},

		setLocale() {
			if (typeof _spPageContextInfo !== 'undefined') {
				this.$i18n.locale = _spPageContextInfo.currentUICultureName.split('-').shift();
			} else {
				this.$i18n.locale = getBrowserLocale();
			}
		},

		setFullScreen() {
			if (document.body.clientWidth <= 1366) {
				let elements = document.querySelectorAll('[data-icon-name="FullScreen"]');

				if (elements.length) {
					elements[0].parentElement.parentElement.click();
					if (this.stateMachine.verboseMode) console.log('[PRODUCTS] FullScreen element clicked');
				}
			}
		},

		login() {
			this.authService.logout();
		},
		handleLoginSuccess() {
			console.log('[Auth] Local login successful');
			this.showLoginSelection = false;
			this.getToken();
		},

		getToken() {
			this.$AuthService.getToken().then(
				(token) => {
					this.stateMachine.token = token;
					this.setHeaderToken(token);
					this.continueProcess();
				},
				(error) => {
					console.error('[Auth] Error getting token:', error);
					const status = error.response && error.response.status;
					if (status === 401 || status === 403) {
						this.login();
					} else {
						this.stateMachine.accessError = true;
						this.stateMachine.loadingInitialData = false;
					}
				},
			);
		},

		continueProcess() {
			this.setCurrentUserId(this.authService.getUser());
			this.removeCanvasMaxWidth();
			this.removeUnwantedElements();
			this.setFullScreen();
			// this.setQueryStringVariables();
			this.setLocale();
			this.setStateVariables();
			this.getStaticData().then(() => {
				this.transferCookiesToLocalStorage();
				this.getLocalStorageData();

				this.getUsageTrackerSessionInfo();
				this.initUsageTracker();
				this.flushUsageTrackerInfo();

				this.setCategoryDisplayed();
				this.stateMachine.initialized = true;
				this.stateMachine.loadingInitialData = false;

				if (!this.userListReleaseNotesCompleted) {
					this.showReleaseNotesModal = true;
				}
			}).catch((e) => console.error('[PRODUCTS] Error at App.beforeMount', e));
		},
	},

	created() {
		console.clear();

		this.authService = this.$AuthService;

		const tokenValidation = this.authService.validateAppsToken();

		if (tokenValidation === 'valid') {
			this.getToken();
		} else if (tokenValidation === 'no_permission') {
			this.stateMachine.accessError = true;
			this.stateMachine.loadingInitialData = false;
		} else {
			this.stateMachine.loadingInitialData = false;
			const { hostname } = window.location;
			if (hostname.includes('products') || hostname === 'localhost' || hostname === '127.0.0.1') {
				this.showLoginSelection = true;
			} else {
				this.login();
			}
		}
	},

	mounted() {
		this.setCssClasses();
		document.title = 'Products HomePage';
	},
};
</script>

<style lang="scss">
@font-face {
	font-family: 'Univers LT Std BoldCn';
	src: url('https://storage.googleapis.com/sharepoint-assets/webfonts/UniversLTStd-BoldCn.woff2')
			format('woff2'),
		url('https://storage.googleapis.com/sharepoint-assets/webfonts/UniversLTStd-BoldCn.woff')
			format('woff');
	font-weight: bold;
	font-style: normal;
}

@font-face {
	font-family: 'Univers LT Std Cn';
	src: url('https://storage.googleapis.com/sharepoint-assets/webfonts/UniversLTStd-Cn.woff2')
			format('woff2'),
		url('https://storage.googleapis.com/sharepoint-assets/webfonts/UniversLTStd-Cn.woff')
			format('woff');
	font-weight: normal;
	font-style: normal;
}

$nav-tabs-active-background-color: #efefef;
$nav-tabs-active-border-color: #7f7f7f;
$nav-tabs-hover-border-color: #afafaf;
$nav-tabs-inactive-border-color: #ccc;
$nav-tabs-link-active-shadow-color: #666;
$table-header-background-color: #efefef;
$table-border-color: #ccc;

#ProductsHomepageAppContainer {
	position: relative;
}



@keyframes bounce {
	0%,
	20%,
	50%,
	80%,
	100% {
		transform: scale(1);
	}
	40% {
		transform: scale(2);
	}
	60% {
		transform: scale(1.5);
	}
}

.bounce {
	animation-duration: 1s;
	animation-name: bounce;
}

.bs-slc {
	font-family: arial;
	padding: 1rem 2rem;
	.modal {
		background-color: rgba(0, 0, 0, 0.25);
		.modal-body {
			max-height: 600px;
			overflow-y: auto;
		}
	}
	.modal-release-notes {
		.modal {
			.modal-body {
				max-height: auto;
			}
			.modal-content {
				height: 660px;
			}
		}
	}

	.modal-header {
		align-items: stretch;
		flex-direction: column;
	}
	.nav-tabs {
		background-color: #fff;
		border-bottom-color: $nav-tabs-active-border-color;
		position: relative;
		z-index: 1;
		.nav-item {
			margin-right: 0.25rem;
		}
		.nav-link {
			border-color: $nav-tabs-inactive-border-color
				$nav-tabs-inactive-border-color $nav-tabs-active-border-color
				$nav-tabs-inactive-border-color;
			&.active {
				background-color: $nav-tabs-active-background-color;
				border-color: $nav-tabs-active-border-color
					$nav-tabs-active-border-color
					$nav-tabs-active-background-color
					$nav-tabs-active-border-color;
				box-shadow: 1px 2px 3px $nav-tabs-link-active-shadow-color;
				color: #000;
				font-weight: 500;
				&:focus,
				&:hover {
					border-color: $nav-tabs-active-border-color
						$nav-tabs-active-border-color
						$nav-tabs-active-background-color
						$nav-tabs-active-border-color;
				}
			}
			&:focus,
			&:hover {
				border-color: $nav-tabs-hover-border-color
					$nav-tabs-hover-border-color $nav-tabs-active-border-color
					$nav-tabs-hover-border-color;
			}
		}
	}
	table.table {
		thead {
			tr {
				background-color: $table-header-background-color !important;
				position: sticky;
				top: 0;
				z-index: 2;
			}
		}
		th {
			background-color: $table-header-background-color !important;
			border-bottom: 2px solid $table-border-color;
			border-top: 0 !important;
			font-weight: 500;
			padding: 0.75rem 0.5rem;
			white-space: nowrap;
		}
		td {
			background-color: transparent !important;
			border-bottom: 1px solid $table-border-color;
			border-top: 0px;
			padding: 0.5rem;
		}
	}
	.table-hover {
		tbody {
			tr {
				&:hover {
					background-color: rgba(0, 0, 0, 0.035);
				}
			}
		}
	}
}

.clickable {
	cursor: pointer;
	text-decoration: none;
	&.clickable--no-hover {
		&:hover {
			text-decoration: none;
		}
	}
	&:hover {
		text-decoration: underline;
	}
}

i {
	&.clickable {
		&:hover {
			text-decoration: none;
			transform: scale(1.25);
		}
	}
}

.flip-h {
	transform: scale(-1, 1);
}

.font-size-80pct {
	font-size: 80%;
}
.font-size-125pct {
	font-size: 125%;
}

.font-size-1rem {
	font-size: 1rem;
}

.font-weight-semibold {
	font-weight: 500;
}
.font-weight-semilight {
	font-weight: 300;
}

.has-elements-visible-on-hover {
	.visible-on-hover {
		visibility: hidden;
	}
	&:hover {
		.visible-on-hover {
			visibility: visible;
		}
	}
}

.mouse-events-disabled {
	pointer-events: none;
}

.no-content-placeholder {
	display: block;
	font-size: 150px;
	line-height: 200px;
	margin-top: 50px;
	opacity: 0.2;
}

@keyframes spinning {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.spinning {
	animation: spinning 2s infinite linear;
}

$tooltip-arrow-color: #ccc;

.tippy-tooltip {
	padding: 0rem;
	&.light-theme {
		box-shadow: none;
		&[x-placement^=right] {
			.tippy-arrow {
				border-right: 7px solid $tooltip-arrow-color;
				border-top: 8px solid transparent;
				border-bottom: 8px solid transparent;
			}
		}
		&[x-placement^=left] {
			.tippy-arrow {
				border-left: 7px solid $tooltip-arrow-color;
				border-top: 8px solid transparent;
				border-bottom: 8px solid transparent;
			}
		}
	}
	.tippy-content {
		iframe {
			border: none;
			box-shadow: 1px 1px 3px #999;
		}
	}
}

.visibility-hidden {
	visibility: hidden;
}

.white-space-pre-line {
	white-space: pre-line;
}

.modal-dialog-header {
	background-color: #eee;
}
</style>
